import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'foundations-components/transitional/components/Form';
import { H4 } from 'foundations-components/transitional/components/H4';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { HR } from 'foundations-components/transitional/components/HR';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import FullNameInput from 'ui-addon-i18n/components/FullNameInput';
import FullAddress from '../Address/FullAddress';
import { ROW_BREAKPOINT } from 'payments-checkout-lib/constants/Sizes';
import useWindowSize from '../../hooks/useWindowSize';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import usePageInteractionTracking from '../../hooks/usePageInteractionTracking';
import useCalculateTax, { getShouldCalculateTaxOnAddrFieldChange } from '../../hooks/useCalculateTax';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from 'payments-checkout-lib/constants/PageInteractionTracking';
import { setErrorMessages, getShowAllErrors } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ShippingInfo = () => {
  const {
    width
  } = useWindowSize();
  const {
    formState,
    checkoutSession: checkoutSessionMap,
    dispatch
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const checkoutSession = checkoutSessionMap.toJS();
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const [hasBeenBlurredFirstName, setHasBeenBlurredFirstName] = useState(false);
  const [hasBeenBlurredLastName, setHasBeenBlurredLastName] = useState(false);
  const shippableCountries = checkoutSession.shippableCountries.map(country => country.toLowerCase());
  const hasCalculateAtCheckoutTaxes = checkoutSession.isAutomatedSalesTaxEnabled;
  const {
    debouncedCallCalculateTax: calculateTax
  } = useCalculateTax();
  const shouldCalculateTax = useCallback((name, value) => getShouldCalculateTaxOnAddrFieldChange(name, value, formState.get('shippingAddress').toJS(), checkoutSession), [formState, checkoutSession]);
  const onChange = useCallback((name, {
    target: {
      value
    }
  }) => {
    trackFirstFormFieldInteraction({
      section: INTERACTION_SECTIONS.shippingAddress,
      target: name in INTERACTION_TARGETS ? INTERACTION_TARGETS[name] : name
    });
    dispatch({
      type: 'setShippingAddressField',
      data: {
        name,
        value
      }
    });
    if (shouldCalculateTax(name, value)) {
      const shippingAddress = Object.assign({}, formState.get('shippingAddress').toJS(), {
        line1: formState.get('shippingAddress').get('address'),
        line2: formState.get('shippingAddress').get('address2'),
        postalCode: name === 'postalCode' ? value : formState.get('shippingAddress').get('postalCode'),
        country: name === 'country' ? value : formState.get('shippingAddress').get('country')
      });
      void calculateTax({
        shippingAddress
      });
    }
  }, [dispatch, trackFirstFormFieldInteraction, formState, calculateTax, shouldCalculateTax]);
  const shippingAddress = formState.get('shippingAddress');
  const firstName = shippingAddress.get('firstName');
  const lastName = shippingAddress.get('lastName');
  const isLoading = formState.get('isCheckoutSubmitPending');
  const isFirstNameError = (hasBeenBlurredFirstName || showAllErrors) && !firstName;
  const isLastNameError = (hasBeenBlurredLastName || showAllErrors) && !lastName;
  useEffect(() => {
    reduxDispatch(setErrorMessages({
      emptyFirstName: {
        isError: isFirstNameError
      },
      emptyLastName: {
        isError: isLastNameError
      }
    }));
  }, [isFirstNameError, isLastNameError, reduxDispatch]);
  const firstNameErrorMessage = useGetErrorMessagesAndTranslations().get('emptyFirstName');
  const lastNameErrorMessage = useGetErrorMessagesAndTranslations().get('emptyLastName');
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": "shipping-info",
    children: [/*#__PURE__*/_jsxs(Form, {
      children: [/*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.shippingInfo.header"
          })
        }), hasCalculateAtCheckoutTaxes && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.taxAddressUsed"
        })]
      }), /*#__PURE__*/_jsx(FullNameInput, {
        direction: width >= ROW_BREAKPOINT ? 'row' : 'column',
        "data-test-id": "full-name-input",
        givenNameInput: /*#__PURE__*/_jsx(FormControl, {
          required: true,
          error: isFirstNameError,
          validationMessage: isFirstNameError && firstNameErrorMessage,
          children: /*#__PURE__*/_jsx(TextInput, {
            "data-test-id": "first-name-input",
            value: firstName,
            onChange: event => onChange('firstName', event),
            disabled: isLoading,
            use: "on-dark",
            onBlur: () => setHasBeenBlurredFirstName(true)
          })
        }),
        familyNameInput: /*#__PURE__*/_jsx(FormControl, {
          required: true,
          error: isLastNameError,
          validationMessage: isLastNameError && lastNameErrorMessage,
          children: /*#__PURE__*/_jsx(TextInput, {
            "data-test-id": "last-name-input",
            value: lastName,
            onChange: event => onChange('lastName', event),
            disabled: isLoading,
            use: "on-dark",
            onBlur: () => setHasBeenBlurredLastName(true)
          })
        })
      }), /*#__PURE__*/_jsx(FullAddress, {
        selectableCountries: shippableCountries,
        fullAddress: shippingAddress.toJS(),
        onChange: onChange,
        isShippingAddress: true
      })]
    }), /*#__PURE__*/_jsx(HR, {})]
  });
};
export default ShippingInfo;